import { action, makeObservable, observable } from "mobx";
import { createContext, useContext } from "react";
import { errorIsUnhandledException } from "src/c8-platform/core/request";
import { SaveDialogProps } from "../SaveDialog";

class SaveDialogStore {
	/** was there an error on save */
	@observable.ref errorOnSave: Error | null = null;

	/** was there an error on validate */
	@observable.ref errorOnValidate: Error | null = null;

	/** confirm to close */
	@observable confirming: boolean = false;

	/** component properties */
	props: Partial<SaveDialogProps>;

	/** is the dialog saving */
	@observable saving: boolean = false;

	/** action to save dialog with validation logic */
	@action onSaveDialog = async (): Promise<void> => {
		const {
			onClose,
			onSave,
			onValidate,
		} = this.props;

		const {
			setSaving,
			setErrorOnSave,
			setErrorOnValidate,
		} = this;

		setSaving(true);

		try {
			// validate dialog content before proceeding to save
			if (onValidate) {
				const valid = await onValidate();
				if (!valid) {
					setSaving(false);
					return;
				}
			}
		} catch (err) {
			// handle validation error
			if (errorIsUnhandledException(err.csiErrorCode)) {
				// unhandled exceptions should not be shown in the dialog
				setErrorOnSave(err);
				// if not message the content inside the dialog shows the validation error rather than showing a error message at the top of the dialog.
			} else if (err.message) {
				setErrorOnValidate(err);
			}

			setSaving(false);
			return;
		}

		setErrorOnValidate(null);

		try {
			// try saving changes
			const ret = await onSave?.() ?? true;
			setSaving(false);

			if (ret) {
				// success and close
				onClose?.();
			}
		} catch (err) {
			// handle save error
			setErrorOnSave(err);
			setSaving(false);
		}
	};

	/** when dialog X button is clicked */
	onCancelDialog = (): void => {
		const { props } = this;
		props.onClose?.();
		props.onCancel?.();
	};

	/** when dialog cancel button is clicked */
	@action onCloseDialog = (): void => {
		if (this.props.confirmClose) {
			this.setConfirming(true);
		} else {
			this.onCancelDialog();
		}
	};

	@action setErrorOnSave = (error: Error | null): void => {
		this.errorOnSave = error;
	};

	@action setErrorOnValidate = (error: Error | null): void => {
		this.errorOnValidate = error;
	};

	@action setConfirming = (confirming: boolean): void => {
		this.confirming = confirming;
	};

	@action setSaving = (saving: boolean): void => {
		this.saving = saving;
	};

	/** set the component properties */
	@action setProps = (newProps: Partial<SaveDialogProps>): void => {
		// merge existing props with the new props
		this.props = { ...this.props, ...newProps };
	};

	constructor() {
		makeObservable(this);
	}
}

export default SaveDialogStore;

export const SaveDialogStoreContext = createContext<SaveDialogStore>(undefined);
export const useSaveDialogStore = (): SaveDialogStore => useContext(SaveDialogStoreContext);
